import {create} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import type {ContentPart} from '../store';
import {hasMeaningfulContent} from '../store';
import {disassemblePromptParts} from './assemblePromptParts';
import type {ComposerFile, FailedComposerImage} from './file';
import type {SelectedRun} from './run';
import {runKey} from './run';
import {createDebouncedSessionStorage} from './storage';

const DRAFT_PERSIST_DEBOUNCE_MS = 400;
const DRAFT_PERSIST_MAX_WAIT_MS = 1000;

export interface Draft {
  body: ContentPart[];
  /**
   * failedFiles are files that failed to read; shown as error thumbnails, never
   * sent and never persisted.
   */
  failedFiles: FailedComposerImage[];
  files: ComposerFile[];
  /**
   * nonce is a sequence that's incremented each time the draft has been reset.
   */
  nonce: number;
  runs: SelectedRun[];
}

export interface DraftState {
  drafts: Record<string, Draft>;

  addFailedFiles: (key: string, failed: FailedComposerImage[]) => void;
  addFiles: (
    key: string,
    files: ComposerFile[],
    // NOTE: `guard` runs inside `set` against the current files, so concurrent
    // adds settle serially instead of racing a stale snapshot.
    guard?: (next: ComposerFile[]) => boolean
  ) => boolean;
  addRun: (key: string, run: SelectedRun) => void;
  removeFailedFile: (key: string, id: string) => void;
  removeFile: (key: string, id: string) => void;
  removeRun: (key: string, runIdentity: string) => void;
  /**
   * reset clears every field and bumps the `nonce` so consumers know to
   * rehydrate. Used on send; returns the draft to a blank slate.
   */
  reset: (key: string) => void;
  setBody: (key: string, body: ContentPart[]) => void;
  /**
   * setDraftParts overwrites a draft from assembled prompt parts, restoring run
   * chips and image attachments. Refuses an occupied draft, returning whether
   * it committed.
   */
  setDraftParts: (key: string, parts: ContentPart[]) => boolean;
}

export function hasPersistableContent(draft: Draft): boolean {
  return hasMeaningfulContent(draft.body) || draft.runs.length > 0;
}

/** draftKey maps a thread id (null for the new-chat view) to its draft key. */
export function draftKey(threadId: string | null): string {
  // NOTE: Prevent collisions between thread IDs and a new chat sentinel value.
  return threadId == null ? 'new-chat' : `thread:${threadId}`;
}

export function createDraftStore() {
  return create<DraftState>()(
    devtools(
      persist(
        set => ({
          drafts: {},
          addFailedFiles: (key, failed) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                failedFiles: [...d.failedFiles, ...failed],
              })),
            })),
          addFiles: (key, files, guard) => {
            let committed = true;

            set(s => {
              const mergedFiles = [...(s.drafts[key]?.files ?? []), ...files];
              if (guard && !guard(mergedFiles)) {
                committed = false;
                return s;
              }

              return {
                drafts: updateDraft(s.drafts, key, d => ({
                  ...d,
                  files: mergedFiles,
                })),
              };
            });

            return committed;
          },
          addRun: (key, run) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d =>
                d.runs.some(r => runKey(r) === runKey(run))
                  ? d
                  : {...d, runs: [...d.runs, run]}
              ),
            })),
          removeFailedFile: (key, id) =>
            set(s => {
              const target = s.drafts[key]?.failedFiles.find(f => f.id === id);
              if (target) {
                URL.revokeObjectURL(target.previewUrl);
              }
              return {
                drafts: updateDraft(s.drafts, key, d => ({
                  ...d,
                  failedFiles: d.failedFiles.filter(f => f.id !== id),
                })),
              };
            }),
          removeFile: (key, id) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                files: d.files.filter(f => f.id !== id),
              })),
            })),
          removeRun: (key, runIdentity) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                runs: d.runs.filter(r => runKey(r) !== runIdentity),
              })),
            })),
          reset: key =>
            set(s => {
              revokeFailedFiles(s.drafts[key]);
              return {
                drafts: updateDraft(s.drafts, key, d => ({
                  ...emptyDraft(),
                  nonce: d.nonce + 1,
                })),
              };
            }),
          setBody: (key, body) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({...d, body})),
            })),
          setDraftParts: (key, parts) => {
            let committed = true;

            set(s => {
              const existing = s.drafts[key];
              if (
                existing &&
                (hasPersistableContent(existing) || existing.files.length > 0)
              ) {
                committed = false;
                return s;
              }

              const {runs, body, images} = disassemblePromptParts(parts);
              // Mint a composer id per image so it re-enters the draft as a file.
              const files: ComposerFile[] = images.map(part => ({
                ...part,
                id: crypto.randomUUID(),
              }));
              revokeFailedFiles(existing);
              return {
                drafts: updateDraft(s.drafts, key, d => ({
                  ...emptyDraft(),
                  body,
                  runs,
                  files,
                  nonce: d.nonce + 1,
                })),
              };
            });

            return committed;
          },
        }),
        {
          name: 'wb-agent-composer-drafts',
          version: 1,
          storage: createJSONStorage(() =>
            createDebouncedSessionStorage({
              debounce: {
                waitMs: DRAFT_PERSIST_DEBOUNCE_MS,
                maxWaitMs: DRAFT_PERSIST_MAX_WAIT_MS,
              },
            })
          ),
          // NOTE: Only text and runs are persisted, and only for drafts that hold
          // meaningful content.
          partialize: state => ({
            drafts: Object.fromEntries(
              Object.entries(state.drafts)
                .filter(([, draft]) => hasPersistableContent(draft))
                .map(([key, draft]) => [
                  key,
                  {body: draft.body, runs: draft.runs},
                ])
            ),
          }),
          merge: (persisted, current) => {
            const saved =
              (persisted as {drafts?: Record<string, Partial<Draft>>})
                ?.drafts ?? {};
            const drafts: Record<string, Draft> = {};
            for (const [key, draft] of Object.entries(saved)) {
              drafts[key] = {
                ...emptyDraft(),
                body: draft.body ?? [],
                runs: draft.runs ?? [],
              };
            }
            return {...current, drafts};
          },
        }
      ),
      {name: 'DraftStore'}
    )
  );
}

export const useDraftStore = createDraftStore();

function emptyDraft(): Draft {
  return {body: [], runs: [], files: [], failedFiles: [], nonce: 0};
}

function revokeFailedFiles(draft: Draft | undefined): void {
  for (const failed of draft?.failedFiles ?? []) {
    URL.revokeObjectURL(failed.previewUrl);
  }
}

/**
 * updateDraft returns a new drafts map with `update` applied to the entry at
 * `key`, seeding an empty draft when the key is absent.
 */
function updateDraft(
  drafts: Record<string, Draft>,
  key: string,
  update: (draft: Draft) => Draft
): Record<string, Draft> {
  return {...drafts, [key]: update(drafts[key] ?? emptyDraft())};
}
